import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCampaigns, type Campaign } from "../api/campaigns";
import type { DistributionRound } from "../api/distributionRounds";

export interface NewRoundDraft {
    name: string;
    campaignId: string;
}

interface DistributionRoundsDialogProps {
    municipalityName: string;
    /** Omgångar som redan är öppna — de lämnas öppna om man inte kryssar i att avsluta dem. */
    openRounds: DistributionRound[];
    /** Förifyllda namn, t.ex. innevarande och nästa månad. */
    initialNames: string[];
    isPending: boolean;
    onCancel: () => void;
    onSubmit: (rounds: NewRoundDraft[], closeOpenRounds: boolean) => void;
}

const MAX_ROUNDS = 10;

/**
 * Startar flera utdelningar på en gång. Poängen med att lägga upp augusti och september
 * tillsammans är att utdelarna får ta ställning till båda i ett och samma formulär —
 * därför lämnas redan öppna omgångar öppna som standard.
 */
export const DistributionRoundsDialog = ({
    municipalityName,
    openRounds,
    initialNames,
    isPending,
    onCancel,
    onSubmit
}: DistributionRoundsDialogProps): JSX.Element => {
    // Ett namn som redan är öppet föreslås inte igen — annars blir det två "Utdelning augusti
    // 2026" den dagen man lägger till september. Blir inget kvar får man en tom rad att fylla i.
    const [drafts, setDrafts] = useState<NewRoundDraft[]>(() => {
        const openNames = new Set(openRounds.map((round) => round.name.trim().toLowerCase()));
        const suggestions = initialNames.filter((name) => !openNames.has(name.trim().toLowerCase()));
        return (suggestions.length > 0 ? suggestions : [""]).map((name) => ({ name, campaignId: "" }));
    });
    const [closeOpenRounds, setCloseOpenRounds] = useState(false);

    const { data: campaigns = [] } = useQuery<Campaign[]>({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        staleTime: 5 * 60 * 1000
    });

    const updateDraft = (index: number, patch: Partial<NewRoundDraft>) =>
        setDrafts((prev) => prev.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));

    const namedDrafts = drafts
        .map((draft) => ({ ...draft, name: draft.name.trim() }))
        .filter((draft) => draft.name.length > 0);

    const canSubmit = namedDrafts.length > 0 && !isPending;

    return (
        <div className="rounds-dialog__backdrop" role="presentation" onClick={onCancel}>
            <div
                className="rounds-dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Ny utdelning"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 className="rounds-dialog__title">
                    Ny utdelning{municipalityName ? ` – ${municipalityName}` : ""}
                </h2>
                <p className="rounds-dialog__intro">
                    Utdelarna ser namnen när de svarar, och kryssar i vilka de kan vara med på.
                </p>

                <div className="rounds-dialog__rows">
                    {drafts.map((draft, index) => (
                        <div key={index} className="rounds-dialog__row">
                            <label className="rounds-dialog__field">
                                Namn
                                <input
                                    type="text"
                                    maxLength={200}
                                    value={draft.name}
                                    disabled={isPending}
                                    placeholder="t.ex. Utdelning september 2026"
                                    onChange={(event) => updateDraft(index, { name: event.target.value })}
                                />
                            </label>
                            <label className="rounds-dialog__field">
                                Kampanj
                                <select
                                    value={draft.campaignId}
                                    disabled={isPending}
                                    onChange={(event) => updateDraft(index, { campaignId: event.target.value })}
                                >
                                    <option value="">Ingen</option>
                                    {campaigns.map((campaign) => (
                                        <option key={campaign.id} value={campaign.id}>
                                            {campaign.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="rounds-dialog__remove"
                                disabled={isPending || drafts.length === 1}
                                title="Ta bort raden"
                                onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}
                            >
                                Ta bort
                            </button>
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    className="rounds-dialog__add"
                    disabled={isPending || drafts.length >= MAX_ROUNDS}
                    onClick={() => setDrafts((prev) => [...prev, { name: "", campaignId: "" }])}
                >
                    + Lägg till utdelning
                </button>

                {openRounds.length > 0 && (
                    <div className="rounds-dialog__open">
                        <p className="rounds-dialog__open-title">
                            Öppna nu: {openRounds.map((round) => round.name).join(", ")}
                        </p>
                        <label className="rounds-dialog__checkbox">
                            <input
                                type="checkbox"
                                checked={closeOpenRounds}
                                disabled={isPending}
                                onChange={(event) => setCloseOpenRounds(event.target.checked)}
                            />
                            Avsluta dem samtidigt (svaren sparas, men de går inte längre att svara på)
                        </label>
                    </div>
                )}

                <div className="rounds-dialog__actions">
                    <button type="button" onClick={onCancel} disabled={isPending}>
                        Avbryt
                    </button>
                    <button
                        type="button"
                        className="rounds-dialog__submit"
                        disabled={!canSubmit}
                        onClick={() => onSubmit(namedDrafts, closeOpenRounds)}
                    >
                        {isPending
                            ? "Skapar…"
                            : `Skapa ${namedDrafts.length} utdelning${namedDrafts.length === 1 ? "" : "ar"}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DistributionRoundsDialog;
